import { InstallationData } from "@/installation/installation";
import { PaginatedResponse } from "@/api/api";

export type Plot = {
    description: string;
    filepath: string;
    title: string;
};

export type RecommendedStatus = {
    name: string;
    action_verb: string;
    action_description: string;
    lucide_icon_id: string;
    requires_reason: boolean;
    available_reasons: { name: string }[];
}

export type OpportunityStatus = {
    name: string;
    description_internal: string;
    description_customer_facing: string;
    is_default_starting_status: boolean;
    visible_to_customers: boolean;
    requires_action: boolean;
    requires_reason: boolean;
    available_reasons: string[];
    recommended_next_statuses: RecommendedStatus[];
    lucide_icon_id: string;
    action_verb: string;
    action_description: string;
}

export type OpportunityDetection = {
    detection_reason?: string;
    severity: number;
    detection_datetime: string;
    search_duration: number;
    savings: OpportunitySaving[];
    plots: Plot[];
    metrics: OpportunityMetric[];
    plot_start_time: string;
    plot_end_time: string;
}

export type OpportunityCategorySeverity = "red" | "yellow";

type RequiredSkill = "high" | "medium" | "low";

type FailureCause = {
    name: string;
    description: string;
    required_skill?: RequiredSkill;
}

export type OpportunityCategory = {
    id: number;
    name: string;
    severity?: OpportunityCategorySeverity;
    description: string;
    default_issue_summary: string;
    default_what_is_happening: string;
    default_text_root_causes: string;
    default_text_mitigations: string;
    attachments: OpportunityAttachment[];
    failure_causes: FailureCause[];
}

export type OpportunityStatusChange = {
    id: number;
    new_status: string;
    occurred_on: string;
    changed_at: string;
    changed_by: {
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        is_staff: boolean;
    }
    comments?: string;
    reason?: string;
}

export const statusChangeDate = (statusChange: OpportunityStatusChange) => statusChange.occurred_on || statusChange.changed_at

export type IssueFeedbackComment = "dont_care" | "not_useful" | "known_issue" | "other";

export type IssueFeedback = {
    id: number;
    user: {
        username: string;
        first_name: string;
        last_name: string;
        email: string;
        is_staff: boolean;
    };
    useful_meter: "thumbs_up" | "thumbs_down";
    comment: IssueFeedbackComment | null;
    text: string;
    created_at: string;
};

export type Opportunity = {
    name: string;
    status: string;
    status_changes: OpportunityStatusChange[];
    issue_summary: string;
    what_is_happening: string;
    installation: InstallationData;
    location: {
        id: number;
        name: string;
    };
    embeddable_report_url: string;
    timestamp: string;
    savings: OpportunitySaving[];
    metrics: OpportunityMetric[];
    category: string;
    identified_date: string;
    last_detected_date?: string;
    number_of_detections: number;
    opportunity_configuration: any;
    attachments: OpportunityAttachment[];
    id: number;
    highest_severity_instance?: OpportunityDetection;
    highest_severity_detection_datetime?: string;
    category_object: OpportunityCategory;
    grafana_url?: string;
    reviewer_notes_internal?: string;
    reviewer_notes_customer_facing?: string;

    severity?: OpportunityCategorySeverity;
    actions?: string;
    potential_root_cause?: string;

    // Add aggregated_metrics field to store aggregated metric data
    aggregated_metrics?: OpportunityMetric[];  // Optional field for aggregated metrics
    // the ordered_detections property
    ordered_detections?: OpportunityDetection[];

    organization_id: number;
    items_to_bring: { name: string }[];
    feedback: IssueFeedback[];
}

export const getFieldWithDefault = (
  opportunity: Opportunity,
  opportunityKey: keyof Opportunity,
  defaultOpportunityCategoryKey?: keyof OpportunityCategory,
  noValueMessage: string = "No value"
)=> {
    return opportunity[opportunityKey] || (defaultOpportunityCategoryKey && opportunity.category_object[defaultOpportunityCategoryKey]) || noValueMessage
}

export const getEffectiveSeverity = (
  opportunity: Opportunity
): OpportunityCategorySeverity | undefined =>
  opportunity.severity ?? opportunity.category_object.severity;

export type OpportunityMetric = {
    name: string;
    unit: string;
    value: number;
}

export type OpportunitySaving = {
    name: string;
    unit: string;
    value: number;
}

export type OpportunityAttachment = {
    image_url: string;
    title: string;
    description: string;
    image_filename: string;
}

export interface OpportunityTableFilters {
    location?: string;
    category?: string;
    organization_id?: number;
    status?: string[];
    installation_ids?: number[];
    requires_action?: boolean;
}

export interface SortingOption {
    id: string;
    desc: boolean;
}

export interface Pagination {
    pageIndex: number;
    pageSize: number;
}

export type OpportunityDataTableResponse = PaginatedResponse<Opportunity> & {
    totals: {
        FRP: number
        ERP: number
        "LL97 Savings": number
    }
}

export type OpportunityFieldGenerationResponse = {
    output: string;
}

export type OpportunityShareTokenGenerationResponse = {
    token: string;
}

export const opportunityCategorySeverityBorderClass: Record<OpportunityCategorySeverity, string> = {
    "red": "border-red-500",
    "yellow": "border-yellow-500"
}

export const opportunityCategorySeverityFillClass: Record<OpportunityCategorySeverity, string> = {
    "red": "fill-red-500",
    "yellow": "fill-yellow-500"
}

export const opportunityCategorySeverityBackgroundClass: Record<OpportunityCategorySeverity, string> = {
    "red": "bg-red-500",
    "yellow": "bg-yellow-500"
}

export const opportunityCategorySeverityTextClass: Record<OpportunityCategorySeverity, string> = {
    "red": "text-red-500",
    "yellow": "text-yellow-500"
}

export const opportunityCategorySeverityTextClassSeverityMessage: Record<OpportunityCategorySeverity, string> = {
    "red": "Service is recommended immediately.",
    "yellow": "Service is recommended at your earliest convenience."
}

export const opportunityCategorySeverityEmoji: Record<OpportunityCategorySeverity, string> = {
    "red": "🔴",
    "yellow": "🟡"
}
